import { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Palette, Plus, Trash2, Loader2, Upload, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const FONTS = ['Inter', 'Georgia', 'Arial', 'Verdana', 'Trebuchet MS', 'Courier New'];
const BLANK = {
  name: '', logo_url: null, primary_color: '#4f46e5', secondary_color: '#0ea5e9',
  accent_color: '#f59e0b', text_color: '#0f172a', font_family: 'Inter',
};

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 rounded border border-zinc-200 cursor-pointer" data-testid={testid} />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 font-mono text-xs" />
    </div>
  </div>
);

export const BrandKitPage = () => {
  const { api } = useAuth();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = not editing; object = editing/creating
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/brand-kits');
      setKits(r.data?.brand_kits || []);
    } catch { toast.error('Failed to load brand kits'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('logo_url', r.data.url);
      toast.success('Logo uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Logo upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give your brand kit a name'); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/brand-kits/${form.id}`, form);
      else await api.post('/brand-kits', form);
      toast.success('Brand kit saved');
      setForm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (kit) => {
    if (!window.confirm(`Delete brand kit "${kit.name}"?`)) return;
    try { await api.delete(`/brand-kits/${kit.id}`); setKits((p) => p.filter((x) => x.id !== kit.id)); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="brand-kit-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2"><Palette className="w-6 h-6 text-indigo-600" /> Brand Kits</h1>
            <p className="text-sm text-zinc-500 mt-1">Logo, colors and fonts applied automatically to your AI creatives.</p>
          </div>
          {!form && (
            <Button onClick={() => setForm({ ...BLANK })} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="new-brand-kit-btn">
              <Plus className="w-4 h-4 mr-2" /> New brand kit
            </Button>
          )}
        </div>

        {form && (
          <Card data-testid="brand-kit-form">
            <CardHeader><CardTitle className="text-base">{form.id ? 'Edit' : 'New'} brand kit</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Acme Coffee" data-testid="brand-kit-name" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ColorField label="Primary" value={form.primary_color} onChange={(v) => set('primary_color', v)} testid="color-primary" />
                <ColorField label="Secondary" value={form.secondary_color} onChange={(v) => set('secondary_color', v)} testid="color-secondary" />
                <ColorField label="Accent" value={form.accent_color} onChange={(v) => set('accent_color', v)} testid="color-accent" />
                <ColorField label="Text" value={form.text_color} onChange={(v) => set('text_color', v)} testid="color-text" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Font</Label>
                  <Select value={form.font_family} onValueChange={(v) => set('font_family', v)}>
                    <SelectTrigger data-testid="brand-kit-font"><SelectValue /></SelectTrigger>
                    <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Logo</Label>
                  <div className="flex items-center gap-3">
                    {form.logo_url && <img src={form.logo_url} alt="logo" className="h-9 object-contain border border-zinc-200 rounded bg-zinc-50 px-1" />}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="brand-kit-logo-input" />
                      <span className="inline-flex items-center text-sm border border-zinc-200 rounded-md px-3 h-9 hover:bg-zinc-50">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} {form.logo_url ? 'Replace' : 'Upload'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              {/* Live swatch preview */}
              <div className="flex gap-2 items-center pt-1">
                <span className="text-xs text-zinc-400">Preview:</span>
                {['primary_color', 'secondary_color', 'accent_color', 'text_color'].map((c) => (
                  <span key={c} className="w-7 h-7 rounded-full border border-zinc-200" style={{ background: form[c] }} />
                ))}
                <span className="text-sm ml-2" style={{ fontFamily: form.font_family }}>Aa — {form.font_family}</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-brand-kit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save
                </Button>
                <Button variant="ghost" onClick={() => setForm(null)}><X className="w-4 h-4 mr-2" /> Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
        ) : kits.length === 0 && !form ? (
          <Card className="border-dashed"><CardContent className="py-12 text-center space-y-2">
            <Palette className="w-10 h-10 text-zinc-300 mx-auto" />
            <p className="font-medium text-zinc-700">No brand kits yet</p>
            <p className="text-xs text-zinc-500">Create one so your AI creatives match your brand automatically.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {kits.map((kit) => (
              <Card key={kit.id} data-testid={`brand-kit-card-${kit.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  {kit.logo_url
                    ? <img src={kit.logo_url} alt="" className="h-10 w-10 object-contain rounded bg-zinc-50 border border-zinc-100" />
                    : <div className="h-10 w-10 rounded" style={{ background: `linear-gradient(135deg, ${kit.primary_color}, ${kit.secondary_color})` }} />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{kit.name}</p>
                    <div className="flex gap-1 mt-1">
                      {[kit.primary_color, kit.secondary_color, kit.accent_color, kit.text_color].map((c, i) => (
                        <span key={i} className="w-4 h-4 rounded-full border border-zinc-200" style={{ background: c }} />
                      ))}
                      <span className="text-[11px] text-zinc-400 ml-2">{kit.font_family}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setForm(kit)} data-testid={`edit-brand-kit-${kit.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(kit)} data-testid={`delete-brand-kit-${kit.id}`}><Trash2 className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BrandKitPage;
